/** Formats a number as USD currency with no decimal places. e.g. "$1,234" */
export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Formats a number as USD currency with 2 decimal places. e.g. "$1,234.56" */
export const currencyFormatterTwoDecimal = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/** Formats a Date to a localized date string (e.g. "2/23/2026" in en-US). */
export const dateFormatter = new Intl.DateTimeFormat("en-US");

/**
 * Formats a Date to a locale string with 12-hour time (e.g. "2/23/2026 3:45 pm").
 * Uses local time zone.
 * @param {Date} date
 * @returns {string}
 */
export function dateFormatterWithTime(date) {
  // Use local time methods instead of UTC methods
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;

  const strTime = hours + ":" + minutes + " " + ampm;

  const month = date.getMonth() + 1;  // getMonth() returns 0-based index
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month}/${day}/${year} ${strTime}`;
}

/** Converts milliseconds to minutes. @param {number} time - ms @returns {number} */
export const toMinutes = (time) => {
  return time / (60 * 1000);
};
/** Converts milliseconds to hours. @param {number} time - ms @returns {number} */
export const toHours = (time) => {
  return time / (1000 * 60 * 60);
};
/** Converts milliseconds to days. @param {number} time - ms @returns {number} */
export const toDays = (time) => {
  return time / (1000 * 60 * 60 * 24);
};
/** Converts milliseconds to weeks. @param {number} time - ms @returns {number} */
export const toWeeks = (time) => {
  return time / (1000 * 60 * 60 * 24 * 7);
};
/** Converts milliseconds to months (approximates 4 weeks/month). @param {number} time - ms @returns {number} */
export const toMonths = (time) => {
  return time / (1000 * 60 * 60 * 24 * 7) / 4;
};
/** Converts milliseconds to years. @param {number} time - ms @returns {number} */
export const toYears = (time) => {
  return time / (1000 * 60 * 60 * 24) / 365;
};

/**
 * Returns a human-readable relative time string from a past date.
 * @param {string|Date} date - The past date
 * @returns {string} e.g. "3 days ago", "yesterday", "2 years ago", or "future" for upcoming dates
 */
export function timeFromDate(date) {
  const now = new Date()
  const startOfDate = new Date(date)
  const difference = now - startOfDate;
  const minutes = Math.floor(toMinutes(difference));
  const hours = Math.floor(toHours(difference));
  const days = Math.floor(toDays(difference));
  const weeks = Math.floor(toWeeks(difference));
  const months = Math.floor(toMonths(difference));
  const years = Math.floor(toYears(difference));
  if (difference < 0) return "future";
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours < 24) return `${hours} hours ago`;
  if (days === 0) return `today`;
  if (days === 1) return `yesterday`;
  if (days < 7) return `${days} days ago`;
  if (weeks === 1) return `${weeks} week ago`;
  if (weeks < 4) return `${weeks} weeks ago `;
  if (months === 1) return `${months} month ago`;
  if (months < 12) return `${months} months ago`;
  if (years === 1) return `1 year ago`;
  if (years > 1.5 && years < 2) return `1.5 years ago`;
  return `${years} years ago`;
}

/**
 * Returns a human-readable time-until string for a future date.
 * Compares at midnight UTC to avoid time-of-day differences.
 * @param {string|Date} date
 * @returns {string} e.g. "3 days", "Tomorrow", "Today", or "Past Due"
 */
export function timeToDate(date) {
  const now = new Date().setUTCHours(0, 0, 0, 0);
  const startOfDate = new Date(date).setUTCHours(0, 0, 0, 0);
  const difference = startOfDate - now;
  const days = Math.floor(toDays(difference));
  const weeks = Math.floor(toWeeks(difference));
  const months = Math.floor(toMonths(difference));
  const years = Math.floor(toYears(difference));
  if (difference < 0) return "Past Due";
  if (days === 0) return `Today`;
  if (days === 1) return `Tomorrow`;
  if (days < 7) return `${days} days`;
  if (weeks === 1) return `${weeks} week`;
  if (weeks < 4) return `${weeks} weeks`;
  if (months === 1) return `${months} month`;
  if (months < 12) return `${months} months`;
  if (years === 1) return `1 year`;
  return `${years} years`;
}

/**
 * Formats a date in Gmail-style:
 * - Today → time only (e.g. "3:45 pm")
 * - This year → abbreviated month + day (e.g. "Feb 23")
 * - Older → full date with year (e.g. "Feb 23, 2024")
 * @param {string|Date|null} date
 * @returns {string} Formatted string, or "-" for null/invalid dates
 */
export function dateFormatterGmailTime(date) {
  // Handle null, undefined, or invalid dates
  if (!date) return '-';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '-';

  let hours = dateObj.getHours();
  let minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? "0" + minutes : minutes;
  const strTime = hours + ":" + minutes + " " + ampm;

  //difference
  const difference =
    new Date().setUTCHours(0, 0, 0, 0) - new Date(dateObj).setUTCHours(0, 0, 0, 0);
  const month = dateObj.toLocaleString("default", { month: "short" });

  if (toDays(difference) > 0) {
    // If more than 365 days ago, include the year
    if (toDays(difference) > 365) {
      return `${month} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
    }
    return `${month} ${dateObj.getDate()}`;
  }
  return strTime;
}


/**
 * Formats bytes into a human-readable file size string.
 * @param {number} bytes - Number of bytes
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {string} Formatted file size (e.g. "1.46 MB")
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (!bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Formats a date string or timestamp into localized format.
 * @param {string|number|Date} date - Date to format
 * @returns {string} Formatted date (e.g. "Feb 23, 2026")
 */
export const formatDate = (date) => {
  if (!date) return '';

  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Returns a short date string like "Nov 2" for the provided date
export const formatDateShort = (date) => {
  if (!date) return '';

  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formats a file name for display, truncating with "..." if it exceeds maxLength.
 * @param {string} fileName - File name to format
 * @param {number} [maxLength=30] - Maximum length before truncating
 * @returns {string} Formatted file name
 */
export const formatFileName = (fileName, maxLength = 30) => {
  if (!fileName) return '';

  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.split('.').pop();
  const nameWithoutExt = fileName.slice(0, -(extension.length + 1));

  const truncatedName = nameWithoutExt.slice(0, maxLength - 3 - extension.length);
  return `${truncatedName}...${extension}`;
};

/**
 * Returns the appropriate file type icon name based on mime type or file extension.
 * @param {string} mimeType - File's mime type
 * @param {string} [fileName=''] - Optional file name for extension fallback
 * @returns {'pdf'|'word'|'excel'|'image'|'document'} Icon name
 */
export const getFileTypeIcon = (mimeType, fileName = '') => {
  const extension = fileName.split('.').pop().toLowerCase();

  // Common document types
  if (mimeType.includes('pdf') || extension === 'pdf') return 'pdf';
  if (mimeType.includes('word') || ['doc', 'docx'].includes(extension)) return 'word';
  if (mimeType.includes('sheet') || ['xls', 'xlsx'].includes(extension)) return 'excel';

  // Image types
  if (mimeType.includes('image/')) return 'image';

  // Default
  return 'document';
};

/** Formats a number with US locale comma separators. e.g. 1234567 → "1,234,567" */
export const numberFormatter = new Intl.NumberFormat("en-US");

export const capitalizeFirstLetter = (string) => {
  return String(string).charAt(0).toUpperCase() + String(string).slice(1);
};

export const underscoreToSpace = (string) => {
  return String(string).replace(/_/g, " ");
};

/**
 * Converts a string to a URL-safe slug (lowercase, hyphens, alphanumeric only).
 * @param {string} [string='']
 * @returns {string} e.g. "Hello World!" → "hello-world"
 */
export const formatToURL = (string = "") => {
  return string
    .toLowerCase() // convert to lowercase
    .replace(/[^\w ]+/g, "") // remove non-alphanumeric characters
    .replace(/\?/g, "") // remove question mark
    .replace(/ +/g, "-"); // replace spaces with hyphens
};

/**
 * Formats a date as YYYY-MM-DD.
 * @param {string|Date} [date='']
 * @returns {string} e.g. "2026-02-23"
 */
export const dateFormatterYYYYMMDD = (date = "") => {
  return new Date(date).toISOString().split("T")[0];
};
